import { DMSItemsResponse } from '@aiwyn/gql-api/src/models/response/dms-items-response.model';
import { OutputDMSItem } from '@aiwyn/gql-api/src/models/response/outputdms-item.model';
import { DMSFileData, DMSFolderData, DMSItemData } from './file-manager.models';
import { DatePipe } from '@angular/common';
import { FileSizePipe } from '../../pipes';

export const ROOT_FOLDER_ID = 'ROOT_FOLDER';

// Type guards
const isFileItem = (item: OutputDMSItem): boolean => item.type === 'FILE';

export function isFileData(item: DMSItemData): item is DMSFileData {
  return item.isFile;
}

export function isFolderData(item: DMSItemData): item is DMSFolderData {
  return !item.isFile;
}

export interface FolderPreviewList {
  files: { id: number; name: string; dmsFileId: string }[];
  initialIndex: number;
}

/**
 * Builds the multi-file preview list for a clicked file: its folder siblings (same parentId),
 * in listing order, mapped to the shape FilePreviewService expects. `items` may span several
 * navigated folders — only the clicked file's folder is included. Falls back to just the
 * clicked file if it isn't part of `items` (e.g. a stale event after a refresh).
 */
export function buildFolderPreviewList(items: DMSItemData[], clicked: DMSFileData): FolderPreviewList {
  const siblings = items.filter((item): item is DMSFileData => isFileData(item) && item.parentId === clicked.parentId);
  const files = (siblings.length ? siblings : [clicked]).map((item) => ({
    id: item.fileId,
    name: item.name,
    dmsFileId: item.id,
  }));
  const initialIndex = Math.max(
    0,
    files.findIndex((item) => item.dmsFileId === clicked.id),
  );
  return { files, initialIndex };
}

/**
 * Resolves the single item a context-menu action (Preview, Details) should operate on.
 *
 * Syncfusion only reports the right-clicked item in the `menuOpen` event; by the time
 * `menuClick` fires, its `fileDetails` has been replaced with the entire multi-checkbox
 * selection (see PP-3278). Single-target actions therefore capture the item on open and
 * resolve it here, falling back to the first selected item only when no target was
 * captured (defensive — `menuOpen` always precedes `menuClick`).
 */
export function resolveContextMenuTarget(
  openedTarget: DMSItemData | undefined,
  clickedSelection: DMSItemData[] | undefined,
): DMSItemData | undefined {
  return openedTarget ?? clickedSelection?.[0];
}

const FILE_EXTENSIONS_MAP: Record<string, string[]> = {
  audio: ['mp3', 'wav'],
  doc: ['doc', 'docx'],
  image: ['jpg', 'jpeg', 'png'],
  pdf: ['pdf'],
  response: [],
  sheet: ['xls', 'xlsx'],
  slides: [],
  table: [],
  txt: ['txt'],
  video: ['mp4', 'mpeg'],
  zip: ['zip', 'tar'],
};

export function getFileIcon(extension: string): string {
  for (const [icon, fileExtensions] of Object.entries(FILE_EXTENSIONS_MAP)) {
    if (fileExtensions.includes(extension)) {
      return icon;
    }
  }

  return 'default-file';
}

// Extended types for mapping
interface MappedItem extends OutputDMSItem {
  parentId?: string;
  hasChild?: boolean;
  fileId?: number; // Present for FILE type items
}

/**
 * Mapping layer that handles parentId and hasChild logic
 */
class DMSResponseMapper {
  static mapResponse(
    response: DMSItemsResponse,
    currentFolderId: string,
    rootLevelFolderIds: Set<string> = new Set(),
  ): { folderPath: MappedItem[]; entries: MappedItem[] } {
    const currentFolder = response.folderPath[response.folderPath.length - 1];
    // The DMS returns the full absolute path (Clients › Client › Projects › …). Anchor the
    // breadcrumb at the portal's own root — the first path item that is one of the folders the
    // initial listing exposed at root — and trim everything above it. Falls back to matching the
    // known root folder id, then to keeping the whole path. This holds for the multi-project root
    // too, where folderPath is always empty and every project folder is a valid anchor.
    let rootLevelFolderIndex = rootLevelFolderIds.size
      ? response.folderPath.findIndex(item => rootLevelFolderIds.has(String(item.id)))
      : -1;
    if (rootLevelFolderIndex === -1) {
      rootLevelFolderIndex = response.folderPath.findIndex(item => String(item.id) === String(currentFolderId));
    }
    if (rootLevelFolderIndex === -1) rootLevelFolderIndex = 0;
    const filteredFolders = response.folderPath.slice(rootLevelFolderIndex);
    const hasChildFolders = response.entries?.some(item => !isFileItem(item));

    // Map folder path with correct hasChild logic
    const mappedFolderPath = filteredFolders.map((item, index) => {
      const isCurrentFolder = index === filteredFolders.length - 1;
      const itemId = item.id != null ? String(item.id) : '';
      const parentId = (index > 0 && filteredFolders[index - 1].id != null) ? String(filteredFolders[index - 1].id) : undefined;
      const name = item.name || (item as any).filename || (item as any).fileName || (item as any).folderName || (item as any).folder_name || (item as any).title || (item as any).dmsItemMetadata?.name || (item as any).metadata?.name || String(item.id) || 'Folder';
      
      return {
        ...item,
        id: itemId,
        name,
        text: name,
        parentId,
        hasChild: isCurrentFolder ? hasChildFolders : true
      };
    });

    // Map entries with correct parentId
    const mappedEntries = response.entries?.map(item => {
      const name = item.name || (item as any).filename || (item as any).fileName || (item as any).folderName || (item as any).folder_name || (item as any).title || (item as any).dmsItemMetadata?.name || (item as any).metadata?.name || String(item.id) || 'File';
      return {
        ...item,
        id: item.id != null ? String(item.id) : '',
        name,
        text: name,
        parentId: currentFolder?.id != null ? String(currentFolder.id) : String(currentFolderId),
        hasChild: false,
      };
    }) || [];

    return {
      folderPath: mappedFolderPath.length ? mappedFolderPath : [{ id: ROOT_FOLDER_ID, name: 'Files', type: 'FOLDER', sequenceId: 0 }],
      entries: mappedEntries
    };
  }
}

/**
 * Factory for creating DMS data objects
 */
export class DMSItemDataFactory {
  static buildFromResponse(
    response: DMSItemsResponse,
    currentFolderId: string,
    { datePipe, fileSizePipe }: { datePipe: DatePipe; fileSizePipe: FileSizePipe },
    rootLevelFolderIds: Set<string> = new Set(),
  ): (DMSFileData | DMSFolderData)[] {
    const mapped = DMSResponseMapper.mapResponse(response, currentFolderId, rootLevelFolderIds);
    const result: (DMSFileData | DMSFolderData)[] = [];

    // Create folder path items
    mapped.folderPath.forEach((item) => {
      result.push(this.createFolder(item));
    });

    // Create entry items
    mapped.entries.forEach((item) => {
      if (isFileItem(item)) {
        result.push(this.createFile(item, { datePipe, fileSizePipe }));
      } else {
        result.push(this.createFolder(item));
      }
    });

    return result;
  }

  private static createFile(item: MappedItem, { datePipe, fileSizePipe }: { datePipe: DatePipe, fileSizePipe: FileSizePipe }): DMSFileData {
    const name = item.name || (item as any).filename || (item as any).fileName;
    return {
      id: String(item.id),
      name,
      type: `.${name?.split('.').pop() || ''}`,
      sequenceId: item.sequenceId,
      isFile: true,
      fileId: item.fileId ?? 0,
      parentId: item.parentId ? String(item.parentId) : undefined,
      hasChild: false,
      permission: {
        copy: true,
        download: true,
        write: false,
        writeContents: false,
        read: true,
        upload: false,
      },
      formattedSize: item.dmsItemMetadata?.size ? fileSizePipe.transform(item.dmsItemMetadata?.size) : null,
      size: item.dmsItemMetadata?.size ?? null,
      uploaded: datePipe.transform(item.dmsItemMetadata?.createdAt, 'short') ?? '',
      uploadedBy: item.dmsItemMetadata?.createdBy?.name,
      modified: datePipe.transform(item.dmsItemMetadata?.modifiedAt, 'short') ?? '',
      modifiedBy: item.dmsItemMetadata?.modifiedBy?.name,
    };
  }

  private static createFolder(item: MappedItem): DMSFolderData {
    return {
      id: String(item.id),
      name: item.name || (item as any).filename || (item as any).fileName,
      sequenceId: item.sequenceId,
      isFile: false,
      parentId: item.parentId ? String(item.parentId) : undefined,
      hasChild: item.hasChild,
      permission: {
        read: true,
        copy: true,
        download: false,
        write: false,
        writeContents: false,
        upload: false,
      },
      size: null,
    };
  }
}
